import InfoTooltip from 'components/InfoTooltip';
import { useMemo } from 'react';
import { Control, FieldValues, UseFormReturn } from 'react-hook-form';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { CustomField, CustomFieldType } from 'types/customFields';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { useClassifiers } from 'utils/useClassifiers';
import CheckboxInput from '../rhf-inputs/CheckboxInput';
import DateInput from '../rhf-inputs/DateInput';
import DecimalInput from '../rhf-inputs/DecimalInput';
import DynamicMultiSelectInput from '../rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from '../rhf-inputs/DynamicSelectInput';
import { CommonInputProps } from '../rhf-inputs/InputTypes';
import MultiSelectInput from '../rhf-inputs/MultiSelectInput';
import NumberInput from '../rhf-inputs/NumberInput';
import SelectInput from '../rhf-inputs/SelectInput';
import TextAreaInput from '../rhf-inputs/TextAreaInput';
import TextInput from '../rhf-inputs/TextInput';
import { CUSTOM_FIELD_PREFIX } from './useCustomFields';

interface Props {
    control: Control<FieldValues>;
    formContext: UseFormReturn<FieldValues>;
    readonly?: boolean;
}

const CustomFieldInput = (props: CustomField & Props) => {
    const {
        control,
        id,
        fieldType,
        description,
        isMandatory,
        isMultivalue,
        label,
        shouldTranslate,
        formContext,
        readonly = false,
    } = props;

    const { setValue } = formContext;

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: fieldType === CustomFieldType.CLASSIFIER ? [props.classifierCode] : [],
    });

    const commonInputProps: CommonInputProps<FieldValues> = {
        control,
        name: String(CUSTOM_FIELD_PREFIX + id),
        label: label ?? undefined,
        required: isMandatory,
        shouldTranslateLabel: shouldTranslate,
        tooltip: description ? <InfoTooltip text={description} /> : undefined,
    };

    const classifierCode = fieldType === CustomFieldType.CLASSIFIER ? props.classifierCode : '';

    const maybeClassifierOptions = useMemo(() => {
        if (fieldType === CustomFieldType.CLASSIFIER) {
            return (
                (getValidOptionsFromClassifier(classifiers[classifierCode]) as {
                    label: string;
                    value: string | number;
                }[]) ?? []
            );
        }
        return [];
    }, [classifiers, fieldType, classifierCode]);

    if (fieldType === CustomFieldType.CLASSIFIER && isLoadingClassifiers) {
        return null;
    }

    switch (fieldType) {
        case CustomFieldType.CLASSIFIER: {
            return isMultivalue ? (
                <MultiSelectInput
                    {...commonInputProps}
                    options={maybeClassifierOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                    setValue={setValue}
                    readonly={readonly}
                    disabled={isLoadingClassifiers}
                />
            ) : (
                <SelectInput
                    {...commonInputProps}
                    options={maybeClassifierOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    setValue={setValue}
                    readonly={readonly}
                    disabled={isLoadingClassifiers}
                />
            );
        }
        case CustomFieldType.BOOLEAN:
            return <CheckboxInput {...commonInputProps} required={false} readonly={readonly} />;
        case CustomFieldType.DATE:
            return <DateInput {...commonInputProps} readonly={readonly} />;
        case CustomFieldType.DECIMAL:
            return <DecimalInput {...commonInputProps} readonly={readonly} />;
        case CustomFieldType.EMPLOYEE:
            return isMultivalue ? (
                <DynamicMultiSelectInput
                    {...commonInputProps}
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                    }}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    setValue={setValue}
                    readonly={readonly ? { linkTo: 'employee' } : false}
                />
            ) : (
                <DynamicSelectInput
                    {...commonInputProps}
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                    }}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    readonly={readonly ? { linkTo: 'employee' } : false}
                    setValue={setValue}
                />
            );
        case CustomFieldType.INTEGER:
            return <NumberInput {...commonInputProps} readonly={readonly} />;
        case CustomFieldType.TEXTAREA:
            return <TextAreaInput {...commonInputProps} readonly={readonly} />;
        case CustomFieldType.TEXTINPUT:
            return <TextInput {...commonInputProps} readonly={readonly} />;
        default: {
            console.error(`Custom input type '${fieldType}' is not yet supported`, {
                id,
                fieldType,
                description,
                label,
            });
            return null;
        }
    }
};

export default CustomFieldInput;
